import type {
  BuilderTranslationField,
  BuilderTranslationLanguage,
  BuilderTranslationValue,
} from "./translationTypes";

export type BuilderTranslationDraftState = {
  baseline: Record<string, string>;
  contextKey: string;
  draft: Record<string, string>;
  resetFields: Set<string>;
};

export function builderTranslationFieldKey(
  field: Pick<BuilderTranslationField, "blockUuid" | "fieldCode">,
): string {
  return `${field.blockUuid}|${field.fieldCode}`;
}

export function builderTranslationValueText(
  value: BuilderTranslationValue,
): string {
  if (typeof value === "string") {
    return value;
  }

  return Array.isArray(value)
    ? value.map((segment) => segment.text).join("")
    : "";
}

function editableFieldText(field: BuilderTranslationField): string {
  return field.valueSource === "translation"
    ? builderTranslationValueText(field.value)
    : "";
}

export function builderTranslationContextKey(
  language: BuilderTranslationLanguage,
): string {
  return JSON.stringify({
    code: language.code,
    fields: language.fields.map(builderTranslationFieldKey).sort(),
  });
}

export function createBuilderTranslationDraft(
  language: BuilderTranslationLanguage | null,
): BuilderTranslationDraftState {
  const baseline = Object.fromEntries(
    (language?.fields ?? []).map((field) => [
      builderTranslationFieldKey(field),
      editableFieldText(field),
    ]),
  );

  return {
    baseline,
    contextKey: language ? builderTranslationContextKey(language) : "",
    draft: { ...baseline },
    resetFields: new Set(),
  };
}

export function reconcileBuilderTranslationDraft(
  current: BuilderTranslationDraftState,
  language: BuilderTranslationLanguage,
): BuilderTranslationDraftState {
  const incoming = createBuilderTranslationDraft(language);

  if (current.contextKey !== incoming.contextKey) {
    return incoming;
  }

  const draft = Object.fromEntries(
    Object.keys(incoming.baseline).map((key) => {
      const isDirty = (
        current.resetFields.has(key)
        || current.draft[key] !== current.baseline[key]
      );

      return [
        key,
        isDirty ? current.draft[key] ?? "" : incoming.draft[key],
      ];
    }),
  );
  const resetFields = new Set(
    [...current.resetFields].filter((key) => (
      Object.prototype.hasOwnProperty.call(incoming.baseline, key)
    )),
  );

  return {
    baseline: incoming.baseline,
    contextKey: incoming.contextKey,
    draft,
    resetFields,
  };
}

export function completeBuilderTranslationDraftSave(
  current: BuilderTranslationDraftState,
  savedContextKey: string,
): BuilderTranslationDraftState {
  if (current.contextKey !== savedContextKey) {
    return current;
  }

  const draft = Object.fromEntries(
    Object.entries(current.draft).map(([key, value]) => [
      key,
      current.resetFields.has(key) ? "" : value,
    ]),
  );

  return {
    baseline: { ...draft },
    contextKey: current.contextKey,
    draft,
    resetFields: new Set(),
  };
}
